const MIN_SCALE = 0.1;

export interface LocalPoint {
  x: number;
  y: number;
}

/**
 * Keep the app root fitted to the visual viewport while giving its
 * children an unzoomed logical canvas. This makes an accidental iOS
 * page zoom survivable: the controller still lays out at the physical
 * screen size instead of the shrunken visualViewport CSS size.
 */
export function installVisualViewportFit(root: HTMLElement): void {
  const docStyle = document.documentElement.style;
  let raf = 0;
  let settleTimer = 0;

  const apply = (): void => {
    raf = 0;
    const vv = window.visualViewport;
    const rawScale = vv?.scale ?? 1;
    const scale = Number.isFinite(rawScale) ? Math.max(MIN_SCALE, rawScale) : 1;
    const visualWidth = Math.max(1, vv?.width ?? window.innerWidth);
    const visualHeight = Math.max(1, vv?.height ?? window.innerHeight);
    const left = vv?.pageLeft ?? window.scrollX;
    const top = vv?.pageTop ?? window.scrollY;
    const logicalWidth = visualWidth * scale;
    const logicalHeight = visualHeight * scale;

    docStyle.setProperty('--pad-vv-left', `${left}px`);
    docStyle.setProperty('--pad-vv-top', `${top}px`);
    docStyle.setProperty('--pad-vv-width', `${logicalWidth}px`);
    docStyle.setProperty('--pad-vv-height', `${logicalHeight}px`);
    docStyle.setProperty('--pad-vv-scale', String(scale));
    docStyle.setProperty('--pad-vv-inverse-scale', String(1 / scale));
    root.dataset.viewportScale = scale.toFixed(3);
  };

  const schedule = (): void => {
    if (!raf) raf = window.requestAnimationFrame(apply);
    if (settleTimer) window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(apply, 120);
  };

  apply();
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
  document.addEventListener('visibilitychange', schedule);
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
}

/**
 * PointerEvent client coordinates are post-transform viewport
 * coordinates. Controls inside the fitted root need pre-transform local
 * coordinates for CSS positioning, so map through the element's rendered
 * rect back into its layout box.
 */
export function localPointInElement(el: HTMLElement, e: PointerEvent): LocalPoint {
  const rect = el.getBoundingClientRect();
  const scaleX = rect.width > 0 ? el.offsetWidth / rect.width : 1;
  const scaleY = rect.height > 0 ? el.offsetHeight / rect.height : 1;
  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY,
  };
}
